import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { useID } from "~/lib/hooks";
import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError } from "~/lib/utils";
import { environment } from "~/models";
import { toast } from "~/ui";
import { Switch } from "~/ui/legacyFellowIndex";

import type { StreamRecordingDisclosureSettingsQuery } from "~/__generated__/StreamRecordingDisclosureSettingsQuery.graphql";
import type {
	StreamRecordingDisclosureSettingsUpdateMutation as MutationType,
	StreamRecordingDisclosureSettingsUpdateMutation$data,
} from "~/__generated__/StreamRecordingDisclosureSettingsUpdateMutation.graphql";

import automationStyles from "./Automations.module.css";

const settingsQuery = graphql`
	query StreamRecordingDisclosureSettingsQuery($streamId: ID!) {
		account {
			recordingDisclosureSettings {
				shouldSendRecordingDisclosureEmail
				allowUsersToToggleRecordingDisclosure
				sendTo
			}
		}
		stream: node(id: $streamId) {
			... on NoteStream {
				id
				recordingDisclosureSettings {
					shouldSendRecordingDisclosureEmail
				}
				autoRecordSetting {
					__typename
					... on NoteStreamUserSettingsBasedAutoRecordEnableSource {
						cannotBeRecordedReason
					}
				}
			}
		}
	}
`;

const StreamRecordingDisclosureSettingsUpdateMutation = graphql`
	mutation StreamRecordingDisclosureSettingsUpdateMutation(
		$streamId: ID!
		$shouldSendRecordingDisclosureEmail: Boolean!
	) {
		updateStreamRecordingDisclosureSettings(
			streamId: $streamId
			shouldSendRecordingDisclosureEmail: $shouldSendRecordingDisclosureEmail
		) {
			stream {
				id
				recordingDisclosureSettings {
					shouldSendRecordingDisclosureEmail
				}
			}
		}
	}
`;

export function StreamRecordingDiscloure({ streamId }: { streamId: string }) {
	const toggleID = useID();
	const { account, stream } = useLazyLoadQuery<StreamRecordingDisclosureSettingsQuery>(
		settingsQuery,
		{
			streamId,
		},
		{
			fetchPolicy: "store-and-network",
		},
	);

	if (stream?.autoRecordSetting?.__typename === "NoteStreamUserSettingsBasedAutoRecordEnableSource") {
		console.log(stream?.autoRecordSetting?.cannotBeRecordedReason);
	}

	const [sendRecordingDisclosureEmail, setSendRecordingDisclosureEmail] = React.useState(
		account.recordingDisclosureSettings.allowUsersToToggleRecordingDisclosure
			? (stream?.recordingDisclosureSettings?.shouldSendRecordingDisclosureEmail ??
					account.recordingDisclosureSettings.shouldSendRecordingDisclosureEmail)
			: account.recordingDisclosureSettings.shouldSendRecordingDisclosureEmail,
	);

	const disabledMessage = React.useMemo(() => {
		if (!stream?.autoRecordSetting) return undefined;
		if (stream?.autoRecordSetting?.__typename === "NoteStreamUserSettingsBasedAutoRecordEnableSource")
			return stream?.autoRecordSetting?.cannotBeRecordedReason;
		return undefined;
	}, [stream?.autoRecordSetting]);

	const sendToText = React.useMemo(() => {
		switch (account.recordingDisclosureSettings.sendTo) {
			case "EXTERNAL":
				return "external invitees";
			case "EXTERNAL_AND_NON_FELLOW_INTERNAL":
				return "external and internal invitees without a Fellow account";
		}
	}, [account.recordingDisclosureSettings.sendTo]);

	const onCheckedChange = React.useCallback(
		(checked: boolean) => {
			commitMutationPromise<MutationType>(environment, {
				mutation: StreamRecordingDisclosureSettingsUpdateMutation,
				variables: { streamId, shouldSendRecordingDisclosureEmail: checked },
			})
				.then((response: StreamRecordingDisclosureSettingsUpdateMutation$data) => {
					if (!response || !response?.updateStreamRecordingDisclosureSettings?.stream.recordingDisclosureSettings) {
						toast.error("Failed to update recording disclosure policy");
					}
					dispatchAnalyticsTrack("Edited Stream Recording Disclosure Setting", {
						// eslint-disable-next-line camelcase
						should_send_recording_disclosure_email: checked,
					});
					setSendRecordingDisclosureEmail(checked);
				})
				.catch(errors => {
					const message = getFirstUserFacingError(errors) ?? "Failed to update recording disclosure policy";
					toast.error(message);
				});
		},
		[streamId],
	);

	return (
		<section className={automationStyles.automationBlock}>
			<div className={automationStyles.row}>
				<h1 className={automationStyles.title}>Recording Disclosure</h1>
			</div>
			<div className={automationStyles.row}>
				<label htmlFor={toggleID} className={automationStyles.label}>
					Send an email 1 hour ahead of meeting to <strong>{sendToText}</strong>, to disclose that the meeting will be
					recorded.
				</label>
				<Switch
					id={toggleID}
					checked={disabledMessage ? false : sendRecordingDisclosureEmail}
					onCheckedChange={onCheckedChange}
					disabled={
						!account.recordingDisclosureSettings.allowUsersToToggleRecordingDisclosure || Boolean(disabledMessage)
					}
					data-disabled={
						!account.recordingDisclosureSettings.allowUsersToToggleRecordingDisclosure || Boolean(disabledMessage)
					}
					disabledTooltip={
						disabledMessage ?? "This setting has been locked by your workspace admin and cannot be changed."
					}
					tooltipPlacement={disabledMessage ? "top" : "right"}
				/>
			</div>
		</section>
	);
}
